import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { ESSENTIAL_MODELS, MODEL_REGISTRY, useDatastore } from '../../hooks/useDatastore';

/**
 * Format a sync duration in milliseconds for display.
 * < 1000ms → "1,234 ms"; otherwise → "1.2 s". Returns the placeholder when absent.
 */
export function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms).toLocaleString()} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * Format an epoch-ms timestamp as a readable local datetime, or the
 * "never this session" placeholder when no full sync has happened yet.
 */
export function formatLastFullSync(epochMs: number | undefined): string {
  if (epochMs === undefined || epochMs === null || !Number.isFinite(epochMs)) return 'never this session';
  return dayjs(epochMs).format('DD MMM YYYY, HH:mm:ss');
}

interface ModelRowStatus {
  label: string;
  color: 'success' | 'warning' | 'default';
}

function deriveStatus(isReady: boolean | undefined, isSyncing: boolean): ModelRowStatus {
  if (isReady) return { label: 'Synced', color: 'success' };
  if (isSyncing) return { label: 'Syncing…', color: 'warning' };
  return { label: 'Pending', color: 'default' };
}

interface ModelGroupProps {
  title: string;
  models: readonly string[];
  modelReadiness: Record<string, boolean>;
  isSyncing: boolean;
  syncedRecordCounts: Record<string, number>;
  syncDurations: Record<string, number>;
  lastFullSyncAt: Record<string, number>;
}

function ModelGroup({
  title,
  models,
  modelReadiness,
  isSyncing,
  syncedRecordCounts,
  syncDurations,
  lastFullSyncAt
}: ModelGroupProps) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Model</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Records (local)</TableCell>
              <TableCell align="right">Time taken</TableCell>
              <TableCell>Last full sync</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((m) => {
              const status = deriveStatus(modelReadiness[m], isSyncing);
              const count = syncedRecordCounts[m];
              return (
                <TableRow key={m}>
                  <TableCell>{m}</TableCell>
                  <TableCell>
                    <Chip size="small" label={status.label} color={status.color} />
                  </TableCell>
                  <TableCell align="right">{count === undefined ? '—' : count.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatDuration(syncDurations[m])}</TableCell>
                  <TableCell>{formatLastFullSync(lastFullSyncAt[m])}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

interface CompactModelGroupProps {
  title: string;
  models: readonly string[];
  modelReadiness: Record<string, boolean>;
  isSyncing: boolean;
  syncDurations: Record<string, number>;
}

/**
 * Dense stacked-list rendering of the same status used by ModelGroup, sized to
 * fit inside a ~360-420px popover — a full Table is too wide for that context.
 */
function CompactModelGroup({ title, models, modelReadiness, isSyncing, syncDurations }: CompactModelGroupProps) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" fontWeight="bold" color="text.secondary">
        {title}
      </Typography>
      <Stack spacing={0.5}>
        {models.map((m) => {
          const status = deriveStatus(modelReadiness[m], isSyncing);
          return (
            <Box
              key={m}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}>
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {m}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatDuration(syncDurations[m])}
              </Typography>
              <Chip size="small" label={status.label} color={status.color} sx={{ minWidth: 68 }} />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

interface SyncDiagnosticsPanelProps {
  /**
   * Renders a dense stacked-list per model group (status chip + time only)
   * instead of the full Table, and drops the header copy — sized to fit a
   * ~360-420px popover. Defaults to false (the full /settings view).
   */
  compact?: boolean;
}

/**
 * Per-model sync diagnostics card (audience: dev/ops). Reads only existing
 * DataStore context state — it derives nothing and triggers no queries.
 *
 * Essential (master data) models hydrate in seconds; the heavy transactional
 * models (SSCC, TripSheet, ...) take far longer — the grouping makes that
 * obvious, which informs a later user-facing sync-window decision.
 */
export default function SyncDiagnosticsPanel({ compact = false }: SyncDiagnosticsPanelProps = {}) {
  const {
    isEssentialReady = false,
    isDatastoreReady = false,
    isSyncing = false,
    modelReadiness = {},
    syncedRecordCounts = {},
    syncDurations = {},
    lastFullSyncAt = {}
  } = useDatastore();

  const heavyModels = MODEL_REGISTRY.filter((m) => !ESSENTIAL_MODELS.includes(m as (typeof ESSENTIAL_MODELS)[number]));

  if (compact) {
    return (
      <Stack spacing={1.5}>
        <CompactModelGroup
          title="Essential (master data)"
          models={ESSENTIAL_MODELS}
          modelReadiness={modelReadiness}
          isSyncing={isSyncing}
          syncDurations={syncDurations}
        />
        <CompactModelGroup
          title="Transactional (background)"
          models={heavyModels}
          modelReadiness={modelReadiness}
          isSyncing={isSyncing}
          syncDurations={syncDurations}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h4" fontWeight="bold">
          Sync diagnostics
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`Essential ready: ${isEssentialReady ? 'yes' : 'no'}`}
            color={isEssentialReady ? 'success' : 'default'}
          />
          <Chip
            size="small"
            label={`Datastore ready: ${isDatastoreReady ? 'yes' : 'no'}`}
            color={isDatastoreReady ? 'success' : 'default'}
          />
          <Chip size="small" label={`Syncing: ${isSyncing ? 'yes' : 'no'}`} color={isSyncing ? 'warning' : 'default'} />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Record counts are the local synced subset, not the full table total.
        </Typography>
      </Stack>

      <ModelGroup
        title="Essential (master data)"
        models={ESSENTIAL_MODELS}
        modelReadiness={modelReadiness}
        isSyncing={isSyncing}
        syncedRecordCounts={syncedRecordCounts}
        syncDurations={syncDurations}
        lastFullSyncAt={lastFullSyncAt}
      />

      <ModelGroup
        title="Transactional (background)"
        models={heavyModels}
        modelReadiness={modelReadiness}
        isSyncing={isSyncing}
        syncedRecordCounts={syncedRecordCounts}
        syncDurations={syncDurations}
        lastFullSyncAt={lastFullSyncAt}
      />
    </Stack>
  );
}
